import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const LicHousingFinanceHomeLoan = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        "question": "What is a home loan EMI?",
        "answer": "An EMI is a fixed monthly payment including both principal and interest, paid until the loan is fully repaid."
      },
      {
        "question": "How is the EMI calculated for LIC Housing Finance?",
        "answer": "Using the standard EMI formula considering loan amount, tenure, and interest rate."
      },
      {
        "question": "Can I reduce my EMI during the loan tenure?",
        "answer": "Yes, through part-prepayment or refinancing."
      },
      {
        "question": "How accurate is TheFinMaster’s EMI calculator?",
        "answer": "It offers highly accurate results based on inputs provided."
      },
      {
        "question": "What’s the benefit of checking EMI before applying?",
        "answer": "It helps you assess affordability and choose the right tenure."
      },
      {
        "question": "Can I change my loan tenure after loan approval?",
        "answer": "You can request it, but it depends on LIC Housing Finance’s policies."
      },
      {
        "question": "Will my EMI change if interest rates change?",
        "answer": "Yes, especially if you have a floating interest rate."
      },
      {
        "question": "Does the calculator include processing fees?",
        "answer": "No, it focuses on principal and interest. Fees are separate."
      },
      {
        "question": "How do I use the EMI calculator?",
        "answer": "Enter the loan amount, tenure, and interest rate to get results."
      },
      {
        "question": "Can I calculate EMI for balance transfer loans?",
        "answer": "Yes, just input the new loan terms."
      },
      {
        "question": "Is the calculator mobile-friendly?",
        "answer": "Yes, it works smoothly on all devices."
      },
      {
        "question": "Does a higher down payment reduce EMI?",
        "answer": "Yes, because you borrow a smaller amount."
      },
      {
        "question": "What if I miss an EMI payment?",
        "answer": "You may face penalties and it may impact your credit score."
      },
      {
        "question": "Can the EMI be auto-debited?",
        "answer": "Yes, most lenders allow auto-debit from your bank account."
      },
      {
        "question": "Is pre-closure allowed in LIC Housing Finance home loans?",
        "answer": "Yes, but check the pre-closure terms and charges."
      }
    ]
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>LIC Housing Finance EMI Calculator – Calculate Your Home Loan EMIs</title>
          <meta  name='description' content='Estimate LIC Housing Finance loan EMIs easily. This calculator helps you forecast repayments and manage your budget efficiently.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/lic-housing-finance-home-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet> 
        <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">
              LIC Housing Finance Home Loan EMI Calculator
            </span>
          </li>
        </ol>
      </nav> */}
         <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
            LIC Housing Finance Home Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
          <div className="w-full md:w-2/3 p-4 mx-auto">
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Plan Your LIC Housing Finance Loan Payments with TheFinMaster’s
              EMI Calculator
            </h2>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              Planning to own your dream home through LIC Housing Finance?
              Before making a financial commitment, it’s important to understand
              what your monthly financial outflow will be. The LIC Housing
              Finance Loan EMI Calculator offered by TheFinMaster simplifies
              this by giving you instant, accurate insights into your monthly
              repayment schedule. Whether you’re a first-time buyer or looking
              to transfer your existing loan, our tool helps you make
              well-informed decisions with utmost clarity and convenience.
            </p>
</div>
            <div className="container mx-auto py-4">
              <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                What is a Home Loan EMI?
              </h2>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                A Home Loan EMI (Equated Monthly Installment) is a fixed monthly
                payment you make to repay your home loan over a specified
                tenure. It comprises two parts—principal repayment and interest
                payment. In the early years of your loan, a larger portion of
                the EMI goes towards interest, while in later years, the
                principal portion increases. EMIs are vital in planning your
                finances as they help you understand your fixed monthly
                obligations and manage your budget effectively.
              </p>
            </div>

            <div className="container mx-auto py-4">
              <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                What is LIC Housing Finance Loan EMI Calculator?
              </h2>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                The LIC Housing Finance Loan EMI Calculator is a smart financial
                tool that helps you compute your monthly loan repayment amount
                with precision. Hosted by TheFinMaster, this calculator is
                designed for ease of use and provides real-time results as you
                adjust values such as loan amount, tenure, and interest rate.
                With just a few clicks, you can visualize your financial
                commitment, compare different scenarios, and prepare a
                sustainable repayment strategy tailored to your lifestyle and
                income.
              </p>
            </div>

            <div className="container mx-auto py-4">
              <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                Why Use TheFinMaster’s LIC Housing Finance Loan EMI Calculator?
              </h2>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  <li>
                    <strong>Instant Results:</strong> Get accurate EMI figures
                    within seconds.
                  </li>
                  <li>
                    <strong>User-Friendly Interface:</strong> Our calculator is
                    designed with simplicity, requiring only basic inputs.
                  </li>
                  <li>
                    <strong>Scenario Comparison:</strong> Modify loan amounts,
                    tenure, and interest rates to explore multiple repayment
                    plans.
                  </li>
                  <li>
                    <strong>Amortization Breakdown:</strong> Visualize the
                    balance between principal and interest over the entire loan
                    period.
                  </li>
                  <li>
                    <strong>Smart Planning:</strong> Helps you align your
                    homeownership goals with financial capacity.
                  </li>
                  <li>
                    <strong>No Registration Required:</strong> Absolutely free
                    with no personal details needed.
                  </li>
                </ul>
              </p>
            </div>

            <div className="container mx-auto py-4">
              <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                How Does the LIC Housing Finance Loan EMI Calculator Work?
              </h2>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                The calculator functions using the standard EMI formula:
              </p>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                EMI = [P x R x (1+R)^N] / [(1+R)^N – 1]
              </p>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                Where:
              </p>
              <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <li>P = Principal Loan Amount</li>
                <li>
                  R = Monthly Interest Rate (Annual Interest Rate divided by 12
                  and by 100)
                </li>
                <li>N = Loan Tenure in Months</li>
              </ul>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <strong>Example:</strong>
                <br />
                Suppose you borrow ₹40 lakhs at an interest rate of 8.5% for 20
                years:
                <br />
                P = ₹40,00,000
                <br />
                R = 8.5/12/100 = 0.00708
                <br />
                N = 240 months
                <br />
                Using the formula, your EMI would be approximately ₹34,678.
              </p>
            </div>
 <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
            <div className="container mx-auto py-4">
              <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                What is the Amortisation Schedule?
              </h2>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                An amortisation schedule is a detailed breakdown showing how
                your loan is repaid over time. It displays the monthly EMI split
                between interest and principal, along with the remaining loan
                balance after each payment. This helps borrowers understand the
                repayment pattern and assess their financial commitments
                accurately.
              </p>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <strong>12-Month Amortisation Schedule (Sample)</strong>
              </p>
              {/* <div className="overflow-x-auto">
                <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border px-4 py-2 text-left">Month</th>
                      <th className="border px-4 py-2 text-left">EMI (₹)</th>
                      <th className="border px-4 py-2 text-left">
                        Principal (₹)
                      </th>
                      <th className="border px-4 py-2 text-left">
                        Interest (₹)
                      </th>
                      <th className="border px-4 py-2 text-left">
                        Balance (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border px-4 py-2">1</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">7,912</td>
                      <td className="border px-4 py-2">26,766</td>
                      <td className="border px-4 py-2">39,92,088</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">2</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">7,961</td>
                      <td className="border px-4 py-2">26,717</td>
                      <td className="border px-4 py-2">39,84,127</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">3</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,010</td>
                      <td className="border px-4 py-2">26,668</td>
                      <td className="border px-4 py-2">39,76,117</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">4</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,059</td>
                      <td className="border px-4 py-2">26,619</td>
                      <td className="border px-4 py-2">39,68,058</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">5</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,109</td>
                      <td className="border px-4 py-2">26,569</td>
                      <td className="border px-4 py-2">39,59,949</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">6</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,159</td>
                      <td className="border px-4 py-2">26,519</td>
                      <td className="border px-4 py-2">39,51,790</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">7</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,209</td>
                      <td className="border px-4 py-2">26,469</td>
                      <td className="border px-4 py-2">39,43,581</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">8</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,259</td>
                      <td className="border px-4 py-2">26,419</td>
                      <td className="border px-4 py-2">39,35,322</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">9</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,309</td>
                      <td className="border px-4 py-2">26,369</td>
                      <td className="border px-4 py-2">39,27,013</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">10</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,360</td>
                      <td className="border px-4 py-2">26,318</td>
                      <td className="border px-4 py-2">39,18,653</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">11</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,411</td>
                      <td className="border px-4 py-2">26,267</td>
                      <td className="border px-4 py-2">39,10,242</td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2">12</td>
                      <td className="border px-4 py-2">34,678</td>
                      <td className="border px-4 py-2">8,462</td>
                      <td className="border px-4 py-2">26,216</td>
                      <td className="border px-4 py-2">39,01,780</td>
                    </tr>
                  </tbody>
                </table>
              </div> */}
                    <div className="mt-6">
  {/* Table view for medium and up */}
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="border px-4 py-2 text-left">Month</th>
          <th className="border px-4 py-2 text-left">EMI  (₹)</th>
          <th className="border px-4 py-2 text-left"> Interest   (₹)</th>
          <th className="border px-4 py-2 text-left">Principal(₹)</th>
          <th className="border px-4 py-2 text-left">Outstanding  (₹)</th>
        </tr>
      </thead>
      <tbody>
        {[
          
          ["1", "34,678", "7,912", "26,766", "39,92,088"],
          ["2", "34,678", "7,961", "26,717", "39,84,127"],
          ["3", "34,678", "8,010", "26,668", "39,76,117"],
          ["4", "34,678", "8,059", "26,619", "39,68,058"],
          ["5", "34,678", "8,109", "26,569", "39,59,949"],
          ["6", "34,678", "8,159", "26,519", "39,51,790"],
          ["7", "34,678", "8,209", "26,469", "39,43,581"],
          ["8", "34,678", "8,259", "26,419", "39,35,322"],
          ["9", "34,678", "8,309", "26,369", "39,27,013"],
          ["10", "34,678", "8,360", "26,318", "39,18,653"],
          ["11", "34,678", "8,411", "26,267", "39,10,242"],
          ["12", "34,678", "8,462", "26,216", "39,01,780"]
     
        ].map(([month, emi, interest, principal, balance], index) => (
          <tr key={index}>
            <td className="border px-4 py-2">{month}</td>
            <td className="border px-4 py-2">{emi}</td>
            <td className="border px-4 py-2">{interest}</td>
            <td className="border px-4 py-2">{principal}</td>
            <td className="border px-4 py-2">{balance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

  {/* Card layout for small screens */}
  <div className="md:hidden space-y-4 mt-4">
    {[
               ["1", "34,678", "7,912", "26,766", "39,92,088"],
               ["2", "34,678", "7,961", "26,717", "39,84,127"],
               ["3", "34,678", "8,010", "26,668", "39,76,117"],
               ["4", "34,678", "8,059", "26,619", "39,68,058"],
               ["5", "34,678", "8,109", "26,569", "39,59,949"],
               ["6", "34,678", "8,159", "26,519", "39,51,790"],
               ["7", "34,678", "8,209", "26,469", "39,43,581"],
               ["8", "34,678", "8,259", "26,419", "39,35,322"],
               ["9", "34,678", "8,309", "26,369", "39,27,013"],
               ["10", "34,678", "8,360", "26,318", "39,18,653"],
               ["11", "34,678", "8,411", "26,267", "39,10,242"],
               ["12", "34,678", "8,462", "26,216", "39,01,780"]
    ].map(([month, emi, interest, principal, balance], index) => (
      <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
        <div><span className="font-semibold">Month:</span> {month}</div>
        <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
        <div><span className="font-semibold">   Interest (₹):</span> {interest}</div>
        <div><span className="font-semibold">Principal(₹):</span> {principal}</div>
        <div><span className="font-semibold"> Outstanding  (₹):</span> {balance}</div>
      </div>
    ))}
  </div>
</div>
            </div>

            <div className="container mx-auto py-4">
              <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                Factors Affecting Your Home Loan EMI
              </h2>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  <li>
                    <strong>Loan Amount:</strong> The total loan you borrow has
                    a direct impact on your EMI. A higher principal loan amount
                    will result in a proportionately larger EMI, especially if
                    the interest rate and tenure remain unchanged.
                  </li>
                  <li>
                    <strong>Interest Rate:</strong> Even a slight variation in
                    the interest rate—say 0.5%—can lead to a noticeable
                    difference in your monthly EMI and total loan repayment.
                    Choosing between fixed and floating rates also affects
                    long-term EMI stability.
                  </li>
                  <li>
                    <strong>Loan Tenure:</strong> A longer repayment tenure
                    reduces your monthly EMI burden, making it more affordable
                    month-to-month. However, it increases the total interest
                    outgo over the life of the loan, making the loan more
                    expensive in the long run.
                  </li>
                  <li>
                    <strong>Processing Fees:</strong> Though it doesn’t directly
                    influence your EMI amount, processing fees add to your
                    upfront cost. It can slightly impact your initial budget and
                    should be considered when calculating total cost of
                    borrowing.
                  </li>
                  <li>
                    <strong>Prepayments:</strong> Making part-prepayments during
                    the loan tenure helps in reducing the principal amount. This
                    leads to a decrease in the remaining interest payable and
                    future EMIs, or a shorter loan tenure, depending on lender
                    terms.
                  </li>
                  <li>
                    <strong>Credit Score:</strong> A high credit/CIBIL score
                    (usually 750+) shows financial discipline and increases your
                    chances of securing a lower interest rate. A lower rate
                    means reduced EMIs and overall loan cost.
                  </li>
                  <li>
                    <strong>Income & Profile:</strong> Lenders evaluate your
                    income stability, job profile, and existing liabilities. A
                    steady and high income with fewer liabilities can fetch you
                    better interest rates and favorable terms, indirectly
                    reducing your EMI burden.
                  </li>
                </ol>
              </p>
            </div>
          </div>

          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>

                  {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default LicHousingFinanceHomeLoan;
